import type {
  DashboardCompetitorData,
  DashboardKpi,
  DashboardNpsData,
  DashboardRatingsData,
  DashboardReviewSourceData,
  DashboardSentimentData,
  DashboardSourceStat,
  DashboardTicketTile,
  DashboardTrendData,
  DashboardTrendDataset,
} from '@common/types';

export const GRR_KPI: DashboardKpi = {
  key: 'grr',
  heading: 'GuestRevu Rating (GRR)',
  icon: 'task_alt',
  primary: '86%',
  secondary: 'Number of Reviews',
  delta: { value: '86%', direction: 'up' },
  footnote: '200',
};

export const SURVEY_REVIEW_KPI: DashboardKpi = {
  key: 'survey',
  heading: 'Survey Review Rating',
  icon: 'rate_review',
  primary: '82%',
  secondary: 'Number of Reviews',
  delta: { value: '82%', direction: 'up' },
  footnote: '118 · 16% response rate',
};

export const ONLINE_REVIEW_KPI: DashboardKpi = {
  key: 'online',
  heading: 'Online Review Rating',
  icon: 'public',
  primary: '89%',
  secondary: 'Number of Reviews',
  delta: { value: '90%', direction: 'down' },
  footnote: '82',
};

export const COMPSET_POSITION_KPI: DashboardKpi = {
  key: 'compset',
  heading: 'Compset Position',
  icon: 'leaderboard',
  primary: '#4',
  secondary: 'of 6 competitors',
  delta: { value: '#3', direction: 'down' },
};

export const TRIPADVISOR_SOURCE: DashboardSourceStat = {
  key: 'tripadvisor',
  heading: 'Tripadvisor',
  logo: 'assets/images/logos/tripadvisor.svg',
  connectionStatus: 'connected',
  primary: '#12',
  secondary: 'of 48 Hotels in Whistler',
  tertiary: 'Based on traveller rankings from 1368 reviews',
  rating: 4,
  ratingMax: 5,
};

export const BOOKING_SOURCE: DashboardSourceStat = {
  key: 'booking',
  heading: 'Booking.com',
  logo: 'assets/images/logos/booking.svg',
  connectionStatus: 'needs-auth',
  primary: '92%',
  secondary: 'Based on 292 traveller reviews',
  headerAction: { label: 'Authenticate', icon: 'verified' },
};

export const GOOGLE_SOURCE: DashboardSourceStat = {
  key: 'google',
  heading: 'Google',
  logo: 'assets/images/logos/google.svg',
  connectionStatus: 'connected',
  primary: '100%',
  secondary: 'Based on 1 traveller reviews',
};

export const EXPEDIA_SOURCE: DashboardSourceStat = {
  key: 'expedia',
  heading: 'Expedia',
  logo: 'assets/images/logos/expedia.svg',
  connectionStatus: 'not-connected',
  cta: {
    label: 'Connect Expedia',
    description:
      'Expedia is not connected yet. Connect this source to start pulling reviews into your dashboard.',
    icon: 'link',
  },
};

const TREND_MONTH = {
  categories: [
    'May-2025',
    'Jun-2025',
    'Jul-2025',
    'Aug-2025',
    'Sep-2025',
    'Oct-2025',
    'Nov-2025',
    'Dec-2025',
    'Jan-2026',
    'Feb-2026',
    'Mar-2026',
    'Apr-2026',
    'May-2026',
  ],
  bucketStartIso: [
    '2025-05-01',
    '2025-06-01',
    '2025-07-01',
    '2025-08-01',
    '2025-09-01',
    '2025-10-01',
    '2025-11-01',
    '2025-12-01',
    '2026-01-01',
    '2026-02-01',
    '2026-03-01',
    '2026-04-01',
    '2026-05-01',
  ],
  feedbackBars: [4, 6, 16, 10, 8, 9, 7, 14, 11, 18, 22, 19, 26],
  onlineBars: [10, 8, 12, 7, 6, 9, 5, 11, 8, 13, 16, 14, 18],
  grr: [72, 70, 86, 84, 82, 82, 80, 78, 80, 76, 74, 78, 86],
  feedbackRating: [54, 58, 70, 76, 74, 72, 70, 66, 72, 74, 78, 80, 88],
  onlineRating: [98, 80, 86, 88, 88, 86, 84, 80, 86, 84, 82, 78, 84],
  annotations: [
    { x: 'Jun-2025', label: 'Pool Break' },
    { x: 'Jul-2025', label: 'New Gm' },
    { x: 'Aug-2025', label: 'F&B Manager Changed' },
    { x: 'Sep-2025', label: 'new menu' },
    { x: 'Oct-2025', label: 'New Shower' },
    { x: 'Nov-2025', label: 'New Curtains' },
    { x: 'Dec-2025', label: 'Pool Issue' },
    { x: 'Jan-2026', label: 'New GM Started' },
    { x: 'Feb-2026', label: 'Heated Pool Problem' },
    { x: 'Feb-2026', label: 'SMC' },
    { x: 'Mar-2026', label: 'New Manager Started' },
  ],
};

const TREND_QUARTER = {
  categories: ['Q2-2025', 'Q3-2025', 'Q4-2025', 'Q1-2026', 'Q2-2026'],
  bucketStartIso: ['2025-04-01', '2025-07-01', '2025-10-01', '2026-01-01', '2026-04-01'],
  feedbackBars: [10, 34, 30, 51, 45],
  onlineBars: [18, 25, 25, 37, 32],
  grr: [71, 84, 80, 77, 82],
  feedbackRating: [56, 73, 69, 75, 84],
  onlineRating: [89, 87, 83, 84, 81],
  annotations: [
    { x: 'Q3-2025', label: 'New Gm' },
    { x: 'Q4-2025', label: 'New Curtains' },
    { x: 'Q1-2026', label: 'New Manager Started' },
  ],
};

const TREND_YEAR = {
  categories: ['2021', '2022', '2023', '2024', '2025', '2026'],
  bucketStartIso: ['2021-01-01', '2022-01-01', '2023-01-01', '2024-01-01', '2025-01-01', '2026-01-01'],
  feedbackBars: [78, 96, 112, 128, 140, 156],
  onlineBars: [42, 55, 64, 71, 82, 94],
  grr: [70, 74, 78, 80, 82, 84],
  feedbackRating: [62, 68, 72, 75, 78, 82],
  onlineRating: [80, 82, 84, 85, 84, 86],
  annotations: [
    { x: '2023', label: 'Rebrand Launch' },
    { x: '2025', label: 'New GM' },
  ],
};

const TREND_WEEK = {
  categories: [
    'W10 - 2026',
    'W11 - 2026',
    'W12 - 2026',
    'W13 - 2026',
    'W14 - 2026',
    'W15 - 2026',
    'W16 - 2026',
    'W17 - 2026',
    'W18 - 2026',
    'W19 - 2026',
    'W20 - 2026',
    'W21 - 2026',
  ],
  bucketStartIso: [
    '2026-03-02',
    '2026-03-09',
    '2026-03-16',
    '2026-03-23',
    '2026-03-30',
    '2026-04-06',
    '2026-04-13',
    '2026-04-20',
    '2026-04-27',
    '2026-05-04',
    '2026-05-11',
    '2026-05-18',
  ],
  feedbackBars: [5, 4, 6, 5, 7, 6, 8, 7, 9, 8, 10, 11],
  onlineBars: [3, 4, 3, 5, 4, 5, 6, 5, 7, 6, 7, 8],
  grr: [80, 78, 82, 80, 84, 82, 85, 83, 86, 84, 86, 88],
  feedbackRating: [76, 78, 80, 79, 82, 80, 84, 82, 85, 84, 86, 87],
  onlineRating: [82, 80, 84, 82, 86, 84, 86, 84, 85, 83, 86, 85],
  annotations: [
    { x: 'W14 - 2026', label: 'New Shower' },
    { x: 'W19 - 2026', label: 'Heated Pool Problem' },
  ],
};

const TREND_DAY = {
  categories: [
    '03 May',
    '04 May',
    '05 May',
    '06 May',
    '07 May',
    '08 May',
    '09 May',
    '10 May',
    '11 May',
    '12 May',
    '13 May',
    '14 May',
  ],
  bucketStartIso: [
    '2026-05-03',
    '2026-05-04',
    '2026-05-05',
    '2026-05-06',
    '2026-05-07',
    '2026-05-08',
    '2026-05-09',
    '2026-05-10',
    '2026-05-11',
    '2026-05-12',
    '2026-05-13',
    '2026-05-14',
  ],
  feedbackBars: [1, 2, 1, 3, 2, 2, 3, 4, 2, 3, 5, 4],
  onlineBars: [1, 0, 2, 1, 2, 1, 2, 2, 3, 1, 2, 3],
  grr: [84, 86, 85, 88, 86, 85, 87, 88, 86, 87, 89, 88],
  feedbackRating: [82, 84, 83, 86, 85, 84, 86, 87, 85, 86, 88, 87],
  onlineRating: [86, 88, 87, 90, 88, 87, 89, 88, 86, 87, 88, 86],
  annotations: [
    { x: '04 May', label: 'New Manager Started' },
    { x: '09 May', label: 'SMC Visit' },
  ],
};

const makeTrendDataset = (raw: typeof TREND_MONTH): DashboardTrendDataset => ({
  categories: raw.categories,
  bucketStartIso: raw.bucketStartIso,
  series: [
    {
      name: 'Guest Feedback Reviews',
      type: 'bar',
      color: 'positive',
      yAxisIndex: 1,
      data: raw.feedbackBars,
    },
    {
      name: 'Online Reviews',
      type: 'bar',
      color: 'warning',
      yAxisIndex: 1,
      data: raw.onlineBars,
    },
    { name: 'GuestRevu Rating (GRR)', type: 'line', color: 'brand', data: raw.grr },
    {
      name: 'Feedback Review Rating',
      type: 'line',
      color: 'positive',
      data: raw.feedbackRating,
    },
    { name: 'Online Review Rating', type: 'line', color: 'warning', data: raw.onlineRating },
  ],
  annotations: raw.annotations,
});

export const TREND_ANALYSIS: DashboardTrendData = {
  byView: {
    day: makeTrendDataset(TREND_DAY),
    week: makeTrendDataset(TREND_WEEK),
    month: makeTrendDataset(TREND_MONTH),
    quarter: makeTrendDataset(TREND_QUARTER),
    year: makeTrendDataset(TREND_YEAR),
  },
};

const firstIso = <T extends { bucketStartIso: ReadonlyArray<string> }>(raw: T): string =>
  raw.bucketStartIso[0];
const lastIso = <T extends { bucketStartIso: ReadonlyArray<string> }>(raw: T): string =>
  raw.bucketStartIso[raw.bucketStartIso.length - 1];

export const TREND_COVERAGE: Readonly<
  Record<'day' | 'week' | 'month' | 'quarter' | 'year', { readonly fromIso: string; readonly toIso: string }>
> = {
  day: { fromIso: firstIso(TREND_DAY), toIso: lastIso(TREND_DAY) },
  week: { fromIso: firstIso(TREND_WEEK), toIso: lastIso(TREND_WEEK) },
  month: { fromIso: firstIso(TREND_MONTH), toIso: lastIso(TREND_MONTH) },
  quarter: { fromIso: firstIso(TREND_QUARTER), toIso: lastIso(TREND_QUARTER) },
  year: { fromIso: firstIso(TREND_YEAR), toIso: lastIso(TREND_YEAR) },
};

const NPS_MONTH = {
  categories: TREND_MONTH.categories,
  bucketStartIso: TREND_MONTH.bucketStartIso,
  promoters: [3, 4, 6, 5, 6, 7, 5, 8, 6, 10, 12, 11, 13],
  passives: [2, 2, 3, 2, 3, 3, 2, 4, 3, 4, 5, 4, 6],
  detractors: [1, 1, 2, 2, 1, 2, 1, 3, 2, 3, 4, 3, 4],
  nps: [40, 38, 60, 70, 65, 55, 50, 35, 45, 30, 25, 40, 55],
  annotations: [
    { x: 'Aug-2025', label: 'F&B Manager Changed' },
    { x: 'Sep-2025', label: 'new menu' },
    { x: 'Oct-2025', label: 'room 13' },
    { x: 'Jan-2026', label: 'SMC' },
    { x: 'Feb-2026', label: 'New Shower' },
    { x: 'Feb-2026', label: 'new menu' },
    { x: 'Mar-2026', label: 'New GM Started' },
    { x: 'Apr-2026', label: 'New Manager Started' },
  ],
};

const NPS_QUARTER = {
  categories: TREND_QUARTER.categories,
  bucketStartIso: TREND_QUARTER.bucketStartIso,
  promoters: [13, 18, 19, 33, 35],
  passives: [7, 8, 9, 13, 15],
  detractors: [3, 5, 6, 10, 11],
  nps: [50, 62, 47, 35, 48],
  annotations: [
    { x: 'Q3-2025', label: 'F&B Manager Changed' },
    { x: 'Q1-2026', label: 'New GM Started' },
  ],
};

const NPS_WEEK = {
  categories: TREND_WEEK.categories,
  bucketStartIso: TREND_WEEK.bucketStartIso,
  promoters: [3, 2, 4, 3, 5, 4, 5, 4, 6, 5, 6, 7],
  passives: [1, 2, 1, 2, 1, 2, 2, 1, 2, 2, 2, 3],
  detractors: [1, 1, 1, 0, 1, 0, 1, 1, 1, 1, 1, 1],
  nps: [40, 30, 50, 60, 55, 65, 60, 65, 65, 55, 60, 65],
  annotations: [{ x: 'W17 - 2026', label: 'SMC Visit' }],
};

const NPS_DAY = {
  categories: TREND_DAY.categories,
  bucketStartIso: TREND_DAY.bucketStartIso,
  promoters: [1, 1, 0, 2, 1, 1, 2, 2, 1, 2, 3, 2],
  passives: [0, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1],
  detractors: [0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0],
  nps: [60, 50, 30, 80, 50, 80, 65, 50, 65, 70, 80, 70],
  annotations: [{ x: '07 May', label: 'New Manager Started' }],
};

const NPS_YEAR = {
  categories: TREND_YEAR.categories,
  bucketStartIso: TREND_YEAR.bucketStartIso,
  promoters: [28, 36, 45, 58, 72, 84],
  passives: [12, 14, 17, 20, 24, 28],
  detractors: [10, 9, 9, 8, 7, 6],
  nps: [36, 44, 51, 58, 62, 68],
  annotations: [
    { x: '2023', label: 'Rebrand Launch' },
    { x: '2025', label: 'New GM' },
  ],
};

const makeNpsDataset = (raw: typeof NPS_MONTH): DashboardTrendDataset => ({
  categories: raw.categories,
  bucketStartIso: raw.bucketStartIso,
  series: [
    { name: 'Promoters', type: 'bar', color: 'success', yAxisIndex: 1, data: raw.promoters },
    {
      name: 'Passives',
      type: 'bar',
      color: 'neutralRating',
      yAxisIndex: 1,
      data: raw.passives,
    },
    {
      name: 'Detractors',
      type: 'bar',
      color: 'negative',
      yAxisIndex: 1,
      data: raw.detractors,
    },
    { name: 'NPS', type: 'line', color: 'brand', data: raw.nps },
  ],
  annotations: raw.annotations,
});

export const NPS: DashboardNpsData = {
  score: 53.03,
  target: 80,
  promoters: 45,
  passives: 11,
  detractors: 10,
  donut: [
    { label: 'Promoters', value: 68.18, color: 'success' },
    { label: 'Passives', value: 16.67, color: 'neutralRating' },
    { label: 'Detractors', value: 15.15, color: 'negative' },
  ],
  trendByView: {
    day: makeNpsDataset(NPS_DAY),
    week: makeNpsDataset(NPS_WEEK),
    month: makeNpsDataset(NPS_MONTH),
    quarter: makeNpsDataset(NPS_QUARTER),
    year: makeNpsDataset(NPS_YEAR),
  },
};

export const SENTIMENT: DashboardSentimentData = {
  score: 77,
  target: 80,
  positive: 86,
  neutral: 27,
  negative: 7,
  donut: [
    { label: 'Positive', value: 72, color: 'success' },
    { label: 'Neutral', value: 23, color: 'neutralRating' },
    { label: 'Negative', value: 6, color: 'negative' },
  ],
  trendCategories: TREND_MONTH.categories,
  trendBucketStartIso: TREND_MONTH.bucketStartIso,
  trendSeries: [
    {
      name: 'Sentiment',
      type: 'line',
      color: 'brand',
      data: [72, 78, 76, 70, 74, 72, 70, 68, 72, 70, 66, 64, 60],
    },
  ],
  trendAnnotations: [
    { x: 'Jul-2025', label: 'New Gm' },
    { x: 'Sep-2025', label: 'New Shower' },
    { x: 'Nov-2025', label: 'Pool Issue' },
    { x: 'Jun-2025', label: 'Pool Break' },
    { x: 'Oct-2025', label: 'new menu' },
    { x: 'Dec-2025', label: 'New Curtains' },
    { x: 'Feb-2026', label: 'Heated Pool Problem' },
  ],
};

export const PRIMARY_SERVICE_RATINGS: DashboardRatingsData = {
  target: 95,
  items: [
    {
      label: 'Overall Briefing and Information Supplied',
      value: 100,
      responses: 3,
      responseRate: 2,
    },
    { label: 'Services Used: Accommodation', value: 100, responses: 1, responseRate: 1 },
    {
      label: 'Services Used: Reservation and Reception Services',
      value: 100,
      responses: 1,
      responseRate: 1,
    },
    { label: 'Services Used: Staff Service', value: 100, responses: 1, responseRate: 1 },
    { label: 'Reception service', value: 96, responses: 20, responseRate: 14 },
    { label: 'Wi-Fi', value: 89, responses: 20, responseRate: 14 },
    { label: 'Location', value: 88.16, responses: 76, responseRate: 53 },
    { label: 'Sleep Quality', value: 88.11, responses: 74, responseRate: 51 },
    { label: 'Reservation Services', value: 86.42, responses: 53, responseRate: 37 },
    { label: 'Overall Satisfaction', value: 86.29, responses: 143, responseRate: 99 },
    { label: 'Service Received', value: 86, responses: 80, responseRate: 55 },
    { label: 'Cleanliness', value: 85.38, responses: 78, responseRate: 54 },
    { label: 'Room Quality', value: 85.07, responses: 75, responseRate: 52 },
    { label: 'Value For Money', value: 83.38, responses: 77, responseRate: 53 },
    { label: 'Reception', value: 77.65, responses: 34, responseRate: 24 },
    { label: 'WiFi', value: 74.38, responses: 32, responseRate: 22 },
  ],
};

export const COMPETITOR: DashboardCompetitorData = {
  categories: [
    'Mullion Cove Hotel',
    'Burgh Island Hotel',
    'The Zetter Clerkenwell',
    'The Langdon',
    'Sheep on Sheep Street - Rooms',
    'Tintagel Castle',
  ],
  series: [
    {
      name: 'Rating',
      color: 'brand',
      data: [93, 93, 89, 88, 85, 80],
    },
  ],
};

export const SERVICE_TICKET_TILES: ReadonlyArray<DashboardTicketTile> = [
  {
    key: 'total',
    heading: 'Total Service Tickets',
    icon: 'confirmation_number',
    value: 18,
    valueTone: 'brand',
    badge: { value: '3', direction: 'up' },
  },
  {
    key: 'active',
    heading: 'Active Service Tickets',
    icon: 'confirmation_number',
    value: 13,
    valueTone: 'brand',
    badge: { value: '0', direction: 'up' },
    details: [
      { label: 'Open', value: 0 },
      { label: 'Assigned', value: 13 },
    ],
  },
  {
    key: 'resolved',
    heading: 'Resolved Service Tickets',
    icon: 'confirmation_number',
    value: 5,
    valueTone: 'brand',
    badge: { value: '3', direction: 'up' },
  },
  {
    key: 'alerts',
    heading: 'Alerts',
    icon: 'warning_amber',
    value: 30,
    valueTone: 'brand',
    badge: { value: '50', direction: 'up' },
  },
];

export const REVIEW_SOURCE: DashboardReviewSourceData = {
  rows: [
    {
      key: 'guestrevu',
      label: 'GuestRevu',
      iconSrc: 'assets/images/logos/icon.svg',
      count: 116,
      rating: 83,
    },
    {
      key: 'booking',
      label: 'Booking.com',
      iconSrc: 'assets/images/logos/booking.svg',
      count: 53,
      rating: 90,
    },
    {
      key: 'tripadvisor',
      label: 'Tripadvisor',
      iconSrc: 'assets/images/logos/tripadvisor.svg',
      count: 24,
      rating: 84,
    },
    {
      key: 'expedia',
      label: 'Expedia',
      iconSrc: 'assets/images/logos/expedia.svg',
      count: 4,
      rating: 95,
    },
    {
      key: 'google',
      label: 'Google',
      iconSrc: 'assets/images/logos/google.svg',
      count: 1,
      rating: 100,
    },
  ],
};
